import React, { useEffect, useRef, useState } from 'react';
import { Brain, Gamepad2, Smartphone, Server, ShieldCheck, BarChart3 } from 'lucide-react';
import SceneHeader from './SceneHeader';
import { techCategories, techStats } from '../data/mock';

const iconMap = { Brain, Gamepad2, Smartphone, Server, ShieldCheck, BarChart3 };

const Technologies = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const current = techCategories[active];
  const ActiveIcon = iconMap[current.icon] || Brain;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((x) => { if (x.isIntersecting) x.target.classList.add('in-view'); });
    }, { threshold: 0.1 });
    el.querySelectorAll('.reveal, .reveal-stagger').forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);

  return (
    <section id="technologies" ref={ref} className="relative py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SceneHeader
          chapter="05 / 06"
          subtitle="Stack"
          title={<>Deep tools. <span className="font-serif neon-text">Sharp</span> teams.</>}
          description="40+ technologies in daily rotation. Pick a discipline to see what we wield."
        />

        {/* Marquee row of featured tools */}
        <div className="mt-16 overflow-hidden relative reveal">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black to-transparent z-10" />
          <div className="flex gap-10 whitespace-nowrap animate-marqueeX" style={{ width: 'max-content' }}>
            {[...techCategories.flatMap((c) => c.tags), ...techCategories.flatMap((c) => c.tags)].map((tag, i) => (
              <span key={`${tag}-${i}`} className="font-light text-3xl md:text-5xl text-white/20 hover:text-cyan-300 transition-colors duration-500 tracking-[-0.02em]">
                {tag}
                <span className="text-cyan-400/40 mx-6">/</span>
              </span>
            ))}
          </div>
        </div>

        {/* Interactive category explorer */}
        <div className="mt-24 grid lg:grid-cols-12 gap-8 reveal">
          {/* Categories nav */}
          <div className="lg:col-span-4">
            {techCategories.map((cat, i) => {
              const Icon = iconMap[cat.icon] || Brain;
              const isActive = i === active;
              return (
                <button
                  key={cat.title}
                  data-cursor="hover"
                  onClick={() => setActive(i)}
                  className={`w-full text-left border-t border-white/10 last:border-b py-5 flex items-center gap-5 transition-all duration-500 ${
                    isActive ? 'pl-5 bg-white/[0.02]' : 'pl-0 hover:pl-3 text-white/70'
                  }`}
                >
                  <span className={`font-mono text-[10px] tracking-[0.3em] w-8 ${isActive ? 'text-cyan-400' : 'text-white/30'}`}>
                    0{i + 1}
                  </span>
                  <div className="flex-1">
                    <div className={`font-light text-xl md:text-2xl tracking-[-0.01em] transition-colors duration-500 ${isActive ? 'chromatic-text' : 'text-white/70'}`}>
                      {cat.title}
                    </div>
                  </div>
                  {isActive ? (
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-blinkDot" />
                      <Icon className="w-4 h-4 text-cyan-300" />
                    </div>
                  ) : (
                    <span className="text-white/20 font-mono text-[10px]">{String(cat.tags.length).padStart(2, '0')}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Panel */}
          <div className="lg:col-span-8">
            <div className="relative rounded-2xl reflective overflow-hidden">
              {/* Glow */}
              <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl" />
              <div className="relative p-8 md:p-12">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Discipline</div>
                    <h3 className="mt-2 font-light text-3xl md:text-5xl chromatic-text tracking-[-0.02em]">
                      {current.title}
                    </h3>
                  </div>
                  <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <ActiveIcon className="w-6 h-6 text-cyan-300" strokeWidth={1.6} />
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
                  {current.tags.map((t, i) => (
                    <span
                      key={t}
                      className="text-[11px] uppercase tracking-[0.22em] text-white/70 animate-fadeInUp"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <span className="text-cyan-400/70 mr-2 font-mono">{String(i + 1).padStart(2, '0')}</span>
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-12 pt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-6">
                  {techStats.map((s) => (
                    <div key={s.label}>
                      <div className="font-light text-3xl md:text-4xl chromatic-text tabular-nums tracking-[-0.02em]">{s.value}</div>
                      <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-white/40">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Technologies;
